import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import {
  FiArrowLeft,
  FiArrowRight,
  FiChevronDown,
  FiChevronRight,
  FiEdit3,
  FiFileText,
  FiLayers,
} from 'react-icons/fi';
import { apiUrl, fileUrl } from '../utils/api';
import InlineLoader from './InlineLoader';
import IngresoMuestrasTable from './IngresoMuestrasTable';
import { AuthContext } from '../context/AuthContext';

const IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.webp', '.bmp', '.gif', '.svg'];
const PDF_EXTENSION = '.pdf';

const getFileName = (path = '') => {
  if (!path) {
    return 'archivo';
  }

  const cleanPath = path.split('?')[0];
  const segments = cleanPath.split('/');
  return segments[segments.length - 1] || 'archivo';
};

const getLowerPath = (path = '') => path.split('?')[0].toLowerCase();
const isImageFile = (path = '') => IMAGE_EXTENSIONS.some((extension) => getLowerPath(path).endsWith(extension));
const isPdfFile = (path = '') => getLowerPath(path).endsWith(PDF_EXTENSION);

const formatDateLabel = (value) => {
  if (!value) {
    return 'Sin fecha';
  }

  const parsed = new Date(`${value}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) {
    return 'Sin fecha';
  }

  return new Intl.DateTimeFormat('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(parsed);
};

const formatDateTimeLabel = (value) => {
  if (!value) {
    return 'Sin fecha';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return 'Sin fecha';
  }

  return new Intl.DateTimeFormat('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(parsed);
};

const AttachmentPreview = ({ filePath, title, onOpenImage, onDownload }) => {
  if (!filePath) {
    return null;
  }

  const url = fileUrl(filePath);
  const fileName = getFileName(filePath);
  const image = isImageFile(filePath);
  const pdf = isPdfFile(filePath);

  if (!image && !pdf) {
    return (
      <div className="attachment-preview">
        <div className="attachment-preview__header">
          <strong>{title}</strong>
          <span className="attachment-preview__name">{fileName}</span>
        </div>
        <div className="attachment-preview__actions">
          <a className="btn btn-outline-brand btn-sm" href={url} target="_blank" rel="noreferrer">
            Abrir archivo
          </a>
          <button type="button" className="btn btn-brand btn-sm" onClick={() => onDownload(url, fileName)}>
            Descargar
          </button>
        </div>
      </div>
    );
  }

  if (image) {
    return (
      <div className="attachment-preview">
        <div className="attachment-preview__header">
          <strong>{title}</strong>
          <span className="attachment-preview__name">{fileName}</span>
        </div>
        <button
          type="button"
          className="attachment-preview__thumb-button"
          onClick={() => onOpenImage({ src: url, name: fileName })}
          aria-label={`Ampliar ${fileName}`}
        >
          <img className="attachment-preview__image" src={url} alt={fileName} />
        </button>
        <div className="attachment-preview__actions">
          <button type="button" className="btn btn-outline-brand btn-sm" onClick={() => onOpenImage({ src: url, name: fileName })}>
            Ver con zoom
          </button>
          <button type="button" className="btn btn-brand btn-sm" onClick={() => onDownload(url, fileName)}>
            Descargar imagen
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="attachment-preview">
      <div className="attachment-preview__header">
        <strong>{title}</strong>
        <span className="attachment-preview__name">{fileName}</span>
      </div>
      <div className="attachment-preview__pdf-frame-wrap">
        <iframe
          className="attachment-preview__pdf"
          src={`${url}#toolbar=0&navpanes=0&scrollbar=1`}
          title={`${title} - ${fileName}`}
        />
      </div>
      <div className="attachment-preview__actions">
        <a className="btn btn-outline-brand btn-sm" href={url} target="_blank" rel="noreferrer">
          Abrir PDF
        </a>
        <button type="button" className="btn btn-brand btn-sm" onClick={() => onDownload(url, fileName)}>
          Descargar PDF
        </button>
      </div>
    </div>
  );
};

const InfoItem = ({ label, value }) => (
  <div className="case-detail-info__item">
    <span className="case-detail-info__label">{label}</span>
    <strong className="case-detail-info__value">{value === null || value === undefined || value === '' ? 'Sin dato' : value}</strong>
  </div>
);

const formatPercentage = (numerator, denominator) => {
  if (!Number.isFinite(denominator) || denominator <= 0) return 'No calculable';
  return `${((numerator / denominator) * 100).toLocaleString('es-AR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} %`;
};

const calculateAnimalIndicators = (caso) => {
  const rows = Array.isArray(caso?.animales_situacion) ? caso.animales_situacion : [];
  const muertos = rows.filter((row) => row.tipo === 'MUERTO').reduce((sum, row) => sum + Number(row.cantidad || 0), 0);
  const enfermos = rows.filter((row) => row.tipo === 'ENFERMO').reduce((sum, row) => sum + Number(row.cantidad || 0), 0);
  const total = Number(caso?.cantidad_animales_totales);
  const hasTotal = caso?.cantidad_animales_totales !== null && caso?.cantidad_animales_totales !== '' && Number.isFinite(total);
  const afectados = enfermos + muertos;
  return { rows, muertos, enfermos, afectados, total: hasTotal ? total : null };
};

const ANIMAL_GROUP_LABELS = {
  VACUNO: 'Bovino', OVINO: 'Ovino', CAPRINO: 'Caprino', PORCINO: 'Porcino', EQUINO: 'Equino',
  CAMELIDOS_SUDAMERICANOS: 'Camélidos sudamericanos', CANINO: 'Canino', FELINO: 'Felino',
  AVES_DE_CORRAL: 'Aves de corral', ANIMALES_EXOTICOS: 'Animales exóticos', ANIMALES_SILVESTRES: 'Animales silvestres',
};
const AGE_UNIT_LABELS = { ANIOS: 'años', MESES: 'meses', DIAS: 'días' };
const formatAnimalAge = (row) => row.edad_valor === null || row.edad_valor === undefined
  ? 'Sin especificar'
  : `${row.edad_valor} ${AGE_UNIT_LABELS[row.edad_unidad] || row.edad_unidad || ''}`.trim();

const CollapsibleHeader = ({ title, subtitle, isExpanded, onToggle, kicker, badge, actions }) => (
  <div className="case-collapsible__header">
    <div className="case-collapsible__intro">
      {kicker ? <span className="section-kicker">{kicker}</span> : null}
      <div className="case-collapsible__heading-row">
        <div>
          <h3 className="form-title">{title}</h3>
          {subtitle ? <p className="page-subtitle mt-2 mb-0">{subtitle}</p> : null}
        </div>
        {badge}
      </div>
    </div>
    <div className="case-collapsible__header-actions">
      {actions}
      <button
        type="button"
        className="btn btn-outline-brand case-collapsible__toggle"
        onClick={onToggle}
        aria-expanded={isExpanded}
      >
        {isExpanded ? <FiChevronDown /> : <FiChevronRight />}
        {isExpanded ? 'Reducir' : 'Expandir'}
      </button>
    </div>
  </div>
);

const VistaMuestras = () => {
  const { protocoloGeneral } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { usuario } = useContext(AuthContext);

  const [caso, setCaso] = useState(null);
  const [caseProtocols, setCaseProtocols] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeImage, setActiveImage] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [downloadingUrl, setDownloadingUrl] = useState('');
  const [generatingReport, setGeneratingReport] = useState(false);
  const [deletingSampleId, setDeletingSampleId] = useState('');
  const [deletingResultId, setDeletingResultId] = useState('');
  const [bannerMessage, setBannerMessage] = useState('');
  const [showSampleEntry, setShowSampleEntry] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    summary: false,
    documents: false,
    samples: false,
    finalReport: false,
  });
  const [expandedSamples, setExpandedSamples] = useState({});
  const [expandedResults, setExpandedResults] = useState({});

  const sampleRefs = useRef(new Map());
  const resultRefs = useRef(new Map());

  const canDeleteRecords =
    usuario?.role === 'admin' || Boolean(usuario?.is_superuser) || Boolean(usuario?.eliminacion_autorizada);
  const token = localStorage.getItem('token');
  const muestraActiva =
    location.state?.muestraActiva || location.state?.protocoloMuestra || location.state?.muestraRecienCreada || '';
  const resultadoActivo =
    location.state?.resultadoActivo || location.state?.resultadoRecienCreado || location.state?.resultadoActualizado || '';

  useEffect(() => {
    setLoading(true);
    setShowSampleEntry(false);

    axios
      .get(apiUrl(`/api/ingresos_casos/${protocoloGeneral}/`))
      .then((response) => {
        setCaso(response.data || null);
      })
      .catch((error) => {
        console.error('Error al cargar el caso:', error);
        setCaso(null);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [protocoloGeneral]);

  useEffect(() => {
    let active = true;

    axios
      .get(apiUrl('/api/ingresos_casos/'))
      .then((response) => {
        if (!active) {
          return;
        }

        const orderedProtocols = (Array.isArray(response.data) ? response.data : [])
          .map((item) => item?.protocolo_general)
          .filter(Boolean);
        setCaseProtocols(orderedProtocols);
      })
      .catch((error) => {
        console.error('Error al cargar la lista de casos para la navegacion:', error);
      });

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (location.state?.bannerMessage) {
      setBannerMessage(location.state.bannerMessage);
      return;
    }

    if (location.state?.muestraRecienCreada) {
      setBannerMessage(`La muestra ${location.state.muestraRecienCreada} se agregó correctamente.`);
      return;
    }

    if (location.state?.muestraActualizada) {
      setBannerMessage(`La muestra ${location.state.muestraActualizada} se actualizó correctamente.`);
      return;
    }

    if (location.state?.resultadoRecienCreado) {
      setBannerMessage('El resultado se agregó correctamente.');
      return;
    }

    if (location.state?.resultadosCreadosCantidad) {
      const total = Number(location.state.resultadosCreadosCantidad);
      setBannerMessage(
        total === 1 ? 'Se agregó 1 resultado correctamente.' : `Se agregaron ${total} resultados correctamente.`
      );
      return;
    }

    if (location.state?.resultadoActualizado) {
      setBannerMessage('El resultado se actualizó correctamente.');
      return;
    }

    setBannerMessage('');
  }, [location.state]);

  useEffect(() => {
    if (loading || !caso) {
      return;
    }

    if (muestraActiva) {
      setExpandedSections((current) => ({ ...current, samples: true }));
      setExpandedSamples((current) => ({ ...current, [muestraActiva]: true }));
    }

    if (resultadoActivo) {
      setExpandedSections((current) => ({ ...current, samples: true }));
      setExpandedResults((current) => ({ ...current, [String(resultadoActivo)]: true }));

      const sampleWithResult = (caso.muestras || []).find((muestra) =>
        (muestra.resultados || []).some((resultado) => String(resultado.id) === String(resultadoActivo))
      );

      if (sampleWithResult?.protocolo_muestra) {
        setExpandedSamples((current) => ({ ...current, [sampleWithResult.protocolo_muestra]: true }));
      }
    }
  }, [caso, loading, muestraActiva, resultadoActivo]);

  useEffect(() => {
    if (loading) {
      return;
    }

    if (resultadoActivo && resultRefs.current.has(String(resultadoActivo))) {
      resultRefs.current.get(String(resultadoActivo))?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    if (muestraActiva && sampleRefs.current.has(muestraActiva)) {
      sampleRefs.current.get(muestraActiva)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [loading, muestraActiva, resultadoActivo]);

  useEffect(() => {
    if (!activeImage) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setActiveImage(null);
        setZoomLevel(1);
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [activeImage]);

  const toggleSection = (sectionKey) => {
    setExpandedSections((current) => ({
      ...current,
      [sectionKey]: !current[sectionKey],
    }));
  };

  const toggleSample = (sampleKey) => {
    setExpandedSamples((current) => ({
      ...current,
      [sampleKey]: !current[sampleKey],
    }));
  };

  const toggleResult = (resultKey) => {
    setExpandedResults((current) => ({
      ...current,
      [resultKey]: !current[resultKey],
    }));
  };

  const handleOpenImage = ({ src, name }) => {
    setActiveImage({ src, name });
    setZoomLevel(1);
  };

  const handleCloseImage = () => {
    setActiveImage(null);
    setZoomLevel(1);
  };

  const handleDownload = async (url, fileName) => {
    try {
      setDownloadingUrl(url);
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('No se pudo descargar el archivo:', error);
      window.open(url, '_blank', 'noopener,noreferrer');
    } finally {
      setDownloadingUrl('');
    }
  };

  const imageTransform = useMemo(() => ({ transform: `scale(${zoomLevel})` }), [zoomLevel]);
  const currentCaseIndex = useMemo(
    () => caseProtocols.findIndex((item) => item === protocoloGeneral),
    [caseProtocols, protocoloGeneral]
  );
  const previousCaseProtocol = currentCaseIndex >= 0 ? caseProtocols[currentCaseIndex + 1] || '' : '';
  const nextCaseProtocol = currentCaseIndex > 0 ? caseProtocols[currentCaseIndex - 1] || '' : '';

  const handleCaseNavigation = (targetProtocol) => {
    if (!targetProtocol) {
      return;
    }

    navigate(`/ver-caso/${targetProtocol}`);
  };

  const handleDownloadReport = async () => {
    const finalReport = caso?.archivo_caso;
    try {
      setGeneratingReport(true);
      const reportUrl = finalReport
        ? fileUrl(finalReport)
        : apiUrl(`/api/ingresos_casos/${protocoloGeneral}/informe-docx/`);
      const response = await axios.get(reportUrl, {
        responseType: 'blob',
      });

      const blobUrl = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = finalReport
        ? getFileName(finalReport)
        : caso?.tipo_estudio === 'PACK_TORO'
          ? `informe_pack_toro_${protocoloGeneral}.docx`
          : `informe_muestras_${protocoloGeneral}.docx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('No se pudo descargar el informe:', error);
      window.alert(finalReport ? 'No se pudo descargar el informe final.' : 'No se pudo generar el informe editable.');
    } finally {
      setGeneratingReport(false);
    }
  };

  const handleDeleteSample = async (protocoloMuestra) => {
    const confirmed = window.confirm(`¿Eliminar la muestra ${protocoloMuestra}? Esta acción no se puede deshacer.`);
    if (!confirmed) {
      return;
    }

    try {
      setDeletingSampleId(protocoloMuestra);
      await axios.delete(apiUrl(`/api/ingresos_muestras/${protocoloMuestra}/`), {
        headers: token ? { Authorization: `Token ${token}` } : {},
      });
      setCaso((current) => ({
        ...current,
        muestras: (current?.muestras || []).filter((muestra) => muestra.protocolo_muestra !== protocoloMuestra),
      }));
      window.alert('Muestra eliminada correctamente.');
    } catch (error) {
      console.error('No se pudo eliminar la muestra:', error);
      window.alert('No se pudo eliminar la muestra.');
    } finally {
      setDeletingSampleId('');
    }
  };

  const handleDeleteResult = async (resultadoId) => {
    const confirmed = window.confirm('¿Eliminar este resultado? Esta acción no se puede deshacer.');
    if (!confirmed) {
      return;
    }

    try {
      setDeletingResultId(String(resultadoId));
      await axios.delete(apiUrl(`/api/resultados/${resultadoId}/`), {
        headers: token ? { Authorization: `Token ${token}` } : {},
      });
      setCaso((current) => ({
        ...current,
        muestras: (current?.muestras || []).map((muestra) => ({
          ...muestra,
          resultados: (muestra.resultados || []).filter((resultado) => resultado.id !== resultadoId),
        })),
      }));
      window.alert('Resultado eliminado correctamente.');
    } catch (error) {
      console.error('No se pudo eliminar el resultado:', error);
      window.alert('No se pudo eliminar el resultado.');
    } finally {
      setDeletingResultId('');
    }
  };

  if (loading) {
    return (
      <div className="container mt-4">
        <InlineLoader label="Cargando detalle del caso, muestras y resultados..." />
      </div>
    );
  }

  if (!caso) {
    return <div className="container mt-4 text-danger">Caso no encontrado.</div>;
  }

  const animalIndicators = calculateAnimalIndicators(caso);

  return (
    <>
      <div className="container page-shell">
        <div className="case-detail-nav">
          <div className="case-detail-nav__buttons">
            <button
              type="button"
              className="btn btn-brand case-detail-nav__button"
              onClick={() => handleCaseNavigation(previousCaseProtocol)}
              disabled={!previousCaseProtocol}
            >
              <FiArrowLeft /> Caso anterior
            </button>
            <button
              type="button"
              className="btn btn-brand case-detail-nav__button"
              onClick={() => handleCaseNavigation(nextCaseProtocol)}
              disabled={!nextCaseProtocol}
            >
              Caso siguiente <FiArrowRight />
            </button>
          </div>
          <Link to="/ver-casos" className="btn btn-outline-brand case-detail-nav__button case-detail-nav__button--secondary">
            <FiArrowLeft /> Volver a casos
          </Link>
        </div>

        <div className="page-header">
          <div>
            <span className="section-kicker">Detalle del caso</span>
            <h2 className="page-title">Caso {caso.protocolo_general}</h2>
            <p className="page-subtitle">Revisa el caso completo, sus muestras y los resultados cargados en un solo lugar.</p>
          </div>
          <img src="/logo-inta.png" alt="Logo INTA" className="page-header-logo" />
        </div>

        {bannerMessage ? <div className="alert alert-success">{bannerMessage}</div> : null}
        <div className="mb-4"><Link className="btn btn-brand" to={`/investigacion?caso_referencia=${encodeURIComponent(caso.protocolo_general)}`}>Buscar similares a este caso</Link></div>

        <div className="surface-card mb-4">
          <CollapsibleHeader
            kicker="Detalle del caso"
            title={`Caso ${caso.protocolo_general}`}
            subtitle="Vista resumida del caso. Expande para revisar todos los datos principales y las acciones disponibles."
            isExpanded={expandedSections.summary}
            onToggle={() => toggleSection('summary')}
            badge={<span className="protocol-chip">{caso.protocolo_general}</span>}
          />

          {expandedSections.summary ? (
            <div className="case-detail-hero case-collapsible__body case-collapsible__body--top">
              <div>
                <div className="case-detail-hero__top">
                  <span className="protocol-chip">{caso.protocolo_general}</span>
                  <span className="protocol-chip">Caso: {formatDateLabel(caso.fecha_caso)}</span>
                  <span className="protocol-chip">Ingreso: {formatDateLabel(caso.fecha_ingreso)}</span>
                </div>

                <div className="case-detail-info-grid">
                  <InfoItem label="Propietario" value={caso.propietario} />
                  <InfoItem label="Fecha del caso" value={formatDateLabel(caso.fecha_caso)} />
                  <InfoItem label="Fecha de ingreso" value={formatDateLabel(caso.fecha_ingreso)} />
                  <InfoItem label="Tipo de estudio" value={caso.tipo_estudio_nombre || caso.tipo_estudio} />
                  <InfoItem label="Provincia" value={caso.provincia} />
                  <InfoItem label="Localidad" value={caso.localidad} />
                  <InfoItem label="Dirección" value={caso.direccion} />
                  <InfoItem label="Médico responsable" value={caso.medico_responsable_nombre || caso.medico_responsable} />
                  <InfoItem label="Remitente" value={caso.remitente} />
                </div>

                <div className="case-detail-note mt-3">
                  <strong>Antecedentes / Anamnesis</strong>
                  <p className="mb-0">{caso.antecedentes_anamnesis || 'Sin antecedentes registrados.'}</p>
                </div>

                <div className="case-detail-note mt-3">
                  <strong>Indicadores epidemiológicos</strong>
                  <p className="mb-0">Calculados automáticamente a partir de la situación de los animales registrada.</p>
                </div>
                <div className="case-detail-info-grid mt-3">
                  <InfoItem label="Población total" value={animalIndicators.total === null ? 'No informada' : animalIndicators.total} />
                  <InfoItem label="Enfermos" value={animalIndicators.enfermos} />
                  <InfoItem label="Muertos" value={animalIndicators.muertos} />
                  <InfoItem label="Afectados" value={animalIndicators.afectados} />
                  <InfoItem label="Morbilidad / prevalencia aparente" value={formatPercentage(animalIndicators.enfermos, animalIndicators.total)} />
                  <InfoItem label="Afectación total" value={formatPercentage(animalIndicators.afectados, animalIndicators.total)} />
                  <InfoItem label="Mortalidad" value={formatPercentage(animalIndicators.muertos, animalIndicators.total)} />
                  <InfoItem label="Letalidad" value={formatPercentage(animalIndicators.muertos, animalIndicators.afectados)} />
                </div>
                <div className="form-text mt-2">
                  Morbilidad = enfermos / total; afectación = (enfermos + muertos) / total; mortalidad = muertos / total; letalidad = muertos / afectados.
                </div>

                {animalIndicators.rows.length > 0 ? (
                  <div className="table-responsive mt-3">
                    <table className="table table-sm align-middle mb-0">
                      <thead><tr><th>Situación</th><th>Cantidad</th><th>Edad</th><th>Sexo</th><th>Grupo</th><th>Categoría</th></tr></thead>
                      <tbody>{animalIndicators.rows.map((row) => (
                        <tr key={row.id}>
                          <td>{row.tipo === 'MUERTO' ? 'Muertos' : 'Enfermos'}</td><td>{row.cantidad}</td>
                          <td>{formatAnimalAge(row)}</td><td>{row.sexo === 'MACHO' ? 'Macho' : row.sexo === 'HEMBRA' ? 'Hembra' : 'Sin especificar'}</td>
                          <td>{ANIMAL_GROUP_LABELS[row.grupo_zootecnico] || row.grupo_zootecnico || 'Sin especificar'}</td><td>{row.categoria || 'Sin especificar'}</td>
                        </tr>
                      ))}</tbody>
                    </table>
                  </div>
                ) : <div className="form-text mt-3">No hay grupos de animales cargados para este caso.</div>}
              </div>

              <div className="case-detail-actions">
                {caso.tipo_estudio === 'PACK_TORO' && !caso.archivo_caso ? (
                  <div className="alert subtle-alert subtle-alert--info mb-2" role="status">
                    <strong>Informe Pack toro autogenerado disponible.</strong>
                  </div>
                ) : null}
                <Link
                  to={`/editar-caso/${caso.protocolo_general}`}
                  state={{ returnToCaseDetail: caso.protocolo_general }}
                  className="btn btn-outline-brand"
                >
                  <FiEdit3 /> Editar caso
                </Link>
                {caso.tipo_estudio === 'PACK_TORO' ? (
                  <Link to={`/pack-toro/${caso.protocolo_general}`} className="btn btn-outline-brand">
                    <FiEdit3 /> Completar Pack toro
                  </Link>
                ) : null}
                <button type="button" className="btn btn-outline-brand" onClick={handleDownloadReport} disabled={generatingReport}>
                  <FiFileText /> {generatingReport
                    ? caso.archivo_caso ? 'Descargando informe final...' : 'Generando informe autogenerado...'
                    : caso.archivo_caso
                      ? 'Descargar informe final'
                      : caso.tipo_estudio === 'PACK_TORO'
                        ? 'Descargar informe Pack toro'
                        : 'Descargar informe autogenerado'}
                </button>
              </div>
            </div>
          ) : null}
        </div>

        {caso.documentos?.length > 0 ? (
          <section className="surface-card mb-4">
            <CollapsibleHeader
              kicker="Documentos"
              title="Archivos del caso"
              subtitle={`${caso.documentos.length} archivo(s) adjunto(s).`}
              isExpanded={expandedSections.documents}
              onToggle={() => toggleSection('documents')}
              badge={<span className="protocol-chip">{caso.documentos.length} archivo(s)</span>}
            />

            {expandedSections.documents ? (
              <div className="case-collapsible__body">
                <div className="case-detail-documents">
                  {caso.documentos.map((documento, index) => (
                    <AttachmentPreview
                      key={documento.id || index}
                      filePath={documento.archivo}
                      title={documento.descripcion || `Documento ${index + 1}`}
                      onOpenImage={handleOpenImage}
                      onDownload={handleDownload}
                    />
                  ))}
                </div>
              </div>
            ) : null}
          </section>
        ) : null}

        <section className="surface-card">
          <CollapsibleHeader
            kicker="Muestras"
            title="Muestras y resultados asociados"
            actions={<button type="button" className="btn btn-brand" aria-expanded={showSampleEntry} onClick={() => setShowSampleEntry((current) => !current)}>{showSampleEntry ? 'Ocultar carga de muestras' : 'Agregar muestras'}</button>}
            subtitle={
              caso.muestras?.length > 0
                ? `${caso.muestras.reduce((total, muestra) => total + (Number(muestra.numero_de_muestras) || 0), 0)} muestra(s) en ${caso.muestras.length} protocolo(s).`
                : 'Todavía no hay muestras cargadas para este caso.'
            }
            isExpanded={expandedSections.samples}
            onToggle={() => toggleSection('samples')}
            badge={
              <span className="protocol-chip">
                <FiLayers /> {(caso.muestras || []).reduce((total, muestra) => total + (Number(muestra.numero_de_muestras) || 0), 0)} muestras
              </span>
            }
          />

          <div className="mt-4" hidden={!showSampleEntry}>
            <IngresoMuestrasTable key={caso.protocolo_general} protocoloGeneral={caso.protocolo_general} onSaved={(created) => {
              setCaso((current) => current?.protocolo_general === created[0].protocolo_general
                ? { ...current, muestras: [...(current.muestras || []), ...created] }
                : current);
            }} />
          </div>

          {expandedSections.samples && caso.muestras?.length > 0 ? (
            <div className="case-detail-sample-list case-collapsible__body">
              {caso.muestras.map((muestra) => {
                const isSampleActive = muestra.protocolo_muestra === muestraActiva;
                const isSampleExpanded = Boolean(expandedSamples[muestra.protocolo_muestra]);

                return (
                  <article
                    key={muestra.protocolo_muestra}
                    ref={(node) => {
                      if (node) {
                        sampleRefs.current.set(muestra.protocolo_muestra, node);
                      }
                    }}
                    className={`case-detail-sample-card${isSampleActive ? ' case-detail-sample-card--active' : ''}`}
                  >
                    <div className="case-detail-sample-card__header">
                      <div>
                        <div className="case-detail-sample-card__chips">
                          <span className="protocol-chip">{muestra.protocolo_muestra}</span>
                          {muestra.protocolo_historico && <span className="badge bg-secondary" title="Protocolo histórico conservado">old</span>}
                          <span className="protocol-chip">{muestra.sector || 'Sin sector'}</span>
                        </div>
                        <h4 className="case-detail-sample-card__title">
                          {muestra.tipo_muestra || 'Sin tipo de muestra'} / {muestra.grupo_zootecnico || 'Sin grupo'}
                        </h4>
                        {muestra.identificacion ? <p className="mb-0">Muestra / animal: {muestra.identificacion}</p> : null}
                      </div>

                      <div className="case-detail-card-actions">
                        <button
                          type="button"
                          className="btn btn-outline-brand btn-sm case-collapsible__toggle case-collapsible__toggle--small"
                          onClick={() => toggleSample(muestra.protocolo_muestra)}
                          aria-expanded={isSampleExpanded}
                        >
                          {isSampleExpanded ? <FiChevronDown /> : <FiChevronRight />}
                          {isSampleExpanded ? 'Reducir' : 'Expandir'}
                        </button>
                        <Link
                          to={`/editar-muestra/${muestra.protocolo_muestra}`}
                          state={{ protocoloGeneral: caso.protocolo_general, returnToCaseDetail: true }}
                          className="btn btn-outline-brand btn-sm"
                        >
                          Editar muestra
                        </Link>
                        <Link
                          to="/resultado-diagnostico"
                          state={{
                            protocoloGeneral: caso.protocolo_general,
                            protocoloMuestra: muestra.protocolo_muestra,
                            returnToCaseDetail: true,
                          }}
                          className="btn btn-brand btn-sm"
                          >
                            {muestra.pack_toro || muestra.es_lote_pack_toro ? 'Resultado de otro estudio' : 'Agregar resultado'}
                          </Link>
                          {muestra.pack_toro && <Link className="btn btn-brand btn-sm" to={`/pack-toro/${caso.protocolo_general}?tab=resultados&muestra=${encodeURIComponent(muestra.protocolo_muestra)}`}>Cargar resultados Pack Toro</Link>}
                          {muestra.es_lote_pack_toro && <Link className="btn btn-brand btn-sm" to={`/pack-toro/${caso.protocolo_general}?tab=muestras&lote=${encodeURIComponent(muestra.protocolo_muestra)}`}>Ingresar al sector Pack Toro para cargas individuales</Link>}
                          {muestra.sector === 'PA' ? (
                            <Link
                              to="/parasitologia/dosis-respuesta"
                              state={{
                                protocoloGeneral: caso.protocolo_general,
                                protocoloMuestra: muestra.protocolo_muestra,
                                returnToCaseDetail: true,
                              }}
                              className="btn btn-outline-brand btn-sm"
                            >
                              Calculadora dosis-respuesta
                            </Link>
                          ) : null}
                          {muestra.sector === 'BM' ? (
                            <Link
                              to="/biologia-molecular"
                            state={{
                              protocoloGeneral: caso.protocolo_general,
                              protocoloMuestra: muestra.protocolo_muestra,
                              returnToCaseDetail: true,
                            }}
                            className="btn btn-outline-brand btn-sm"
                          >
                            Cargar ensayo BM
                          </Link>
                        ) : null}
                        {canDeleteRecords ? (
                          <button
                            type="button"
                            className="btn btn-outline-danger btn-sm"
                            onClick={() => handleDeleteSample(muestra.protocolo_muestra)}
                            disabled={deletingSampleId === muestra.protocolo_muestra}
                          >
                            {deletingSampleId === muestra.protocolo_muestra ? 'Eliminando...' : 'Eliminar muestra'}
                          </button>
                        ) : null}
                      </div>
                    </div>

                    {isSampleExpanded ? (
                      <div className="case-collapsible__body">
                        <div className="case-detail-info-grid case-detail-info-grid--sample">
                          <InfoItem label="Fecha de recoleccion" value={formatDateLabel(muestra.fecha_recoleccion)} />
                          <InfoItem label="AISA tomó muestra" value={muestra.aisa_tomo_muestra ? 'Sí' : 'No'} />
                          <InfoItem label="Operario INTA" value={muestra.nombre_operario_inta_nombre || muestra.nombre_operario_INTA} />
                          <InfoItem label="Número de muestras" value={String(muestra.numero_de_muestras || '')} />
                          <InfoItem label="Cantidad de grupos" value={String(muestra.cantidad_grupos_zootecnicos || '')} />
                          <InfoItem label="Fecha de ingreso" value={formatDateTimeLabel(muestra.fecha_hora_ingreso)} />
                        </div>

                        {muestra.observaciones ? (
                          <div className="case-detail-note mt-3">
                            <strong>Observaciones de la muestra</strong>
                            <p className="mb-0">{muestra.observaciones}</p>
                          </div>
                        ) : null}

                        {muestra.archivo_muestra ? (
                          <div className="mt-3">
                            <AttachmentPreview
                              filePath={muestra.archivo_muestra}
                              title="Archivo de la muestra"
                              onOpenImage={handleOpenImage}
                              onDownload={handleDownload}
                            />
                          </div>
                        ) : null}

                        <div className="case-detail-results">
                          <div className="case-detail-results__top">
                            <h5 className="mb-0">Resultado por muestra</h5>
                            <span className="protocol-chip">{muestra.resultados?.length || 0} resultado(s)</span>
                          </div>

                          {muestra.resultados?.length > 0 ? (
                            <div className="case-detail-result-list">
                              {muestra.resultados.map((resultado) => {
                                const resultKey = String(resultado.id);
                                const isResultActive = resultKey === String(resultadoActivo);
                                const isResultExpanded = Boolean(expandedResults[resultKey]);

                                return (
                                  <article
                                    key={resultado.id}
                                    ref={(node) => {
                                      if (node) {
                                        resultRefs.current.set(resultKey, node);
                                      }
                                    }}
                                    className={`case-detail-result-card${isResultActive ? ' case-detail-result-card--active' : ''}`}
                                  >
                                    <div className="case-detail-result-card__header">
                                      <div>
                                        <span className="protocol-chip">Resultado #{resultado.id}</span>
                                        <h6 className="case-detail-result-card__title">
                                          {resultado.tipo_analisis || 'Sin tipo de análisis'}
                                        </h6>
                                      </div>

                                      <div className="case-detail-card-actions">
                                        <button
                                          type="button"
                                          className="btn btn-outline-brand btn-sm case-collapsible__toggle case-collapsible__toggle--small"
                                          onClick={() => toggleResult(resultKey)}
                                          aria-expanded={isResultExpanded}
                                        >
                                          {isResultExpanded ? <FiChevronDown /> : <FiChevronRight />}
                                          {isResultExpanded ? 'Reducir' : 'Expandir'}
                                        </button>
                                        <Link
                                          to={`/editar-resultado/${resultado.id}`}
                                          state={{
                                            protocoloGeneral: caso.protocolo_general,
                                            protocoloMuestra: muestra.protocolo_muestra,
                                            returnToCaseDetail: true,
                                          }}
                                          className="btn btn-outline-brand btn-sm"
                                        >
                                          Editar resultado
                                        </Link>
                                        {canDeleteRecords ? (
                                          <button
                                            type="button"
                                            className="btn btn-outline-danger btn-sm"
                                            onClick={() => handleDeleteResult(resultado.id)}
                                            disabled={deletingResultId === resultKey}
                                          >
                                            {deletingResultId === resultKey ? 'Eliminando...' : 'Eliminar'}
                                          </button>
                                        ) : null}
                                      </div>
                                    </div>

                                    {isResultExpanded ? (
                                      <div className="case-collapsible__body">
                                        <div className="case-detail-info-grid case-detail-info-grid--result">
                                          <InfoItem label="Sector" value={resultado.sector} />
                                          <InfoItem label="Fecha de registro" value={formatDateTimeLabel(resultado.fecha_registro)} />
                                        </div>

                                        <div className="case-detail-note mt-3">
                                          <strong>Resultado</strong>
                                          <p className="mb-0">{resultado.resultado_texto || 'Sin resultado registrado'}</p>
                                        </div>

                                        {resultado.archivo_adjunto ? (
                                          <div className="mt-3">
                                            <AttachmentPreview
                                              filePath={resultado.archivo_adjunto}
                                              title="Adjunto del resultado"
                                              onOpenImage={handleOpenImage}
                                              onDownload={handleDownload}
                                            />
                                          </div>
                                        ) : null}
                                      </div>
                                    ) : null}
                                  </article>
                                );
                              })}
                            </div>
                          ) : (
                            <div className="dashboard-empty dashboard-empty--compact mt-3">
                              {muestra.pack_toro ? 'Los resultados de Pack Toro se consultan en Cargar resultados Pack Toro. No hay resultados de otros estudios.' : 'Esta muestra no tiene resultados registrados.'}
                            </div>
                          )}
                        </div>
                      </div>
                    ) : null}
                  </article>
                );
              })}
            </div>
          ) : expandedSections.samples ? (
            <div className="dashboard-empty mt-4">
              <p className="mb-3">Este caso aun no tiene muestras cargadas.</p>
              <button
                type="button"
                onClick={() => setShowSampleEntry(true)}
                className="btn btn-brand"
              >
                Agregar la primera muestra
              </button>
            </div>
          ) : null}
        </section>

        <section className="surface-card mt-4">
          <CollapsibleHeader
            kicker="Cierre del caso"
            title="Diagnostico e informe final"
            subtitle="Consulta al final de la ficha el diagnóstico definitivo, las conclusiones y el informe cargado."
            isExpanded={expandedSections.finalReport}
            onToggle={() => toggleSection('finalReport')}
            actions={
              <Link
                to={`/informe-final/${caso.protocolo_general}`}
                state={{ protocoloGeneral: caso.protocolo_general, returnToCaseDetail: true }}
                className="btn btn-outline-brand"
              >
                <FiEdit3 /> Editar diagnóstico e informe final
              </Link>
            }
          />

          {expandedSections.finalReport ? (
            <div className="case-collapsible__body">
              <div className="case-detail-final-report">
                <div className="case-detail-final-report__summary">
                  <div className="case-detail-info-grid case-detail-info-grid--final">
                    <InfoItem label="Diagnóstico primario" value={caso.diagnostico_primario} />
                    <InfoItem label="Diagnóstico secundario" value={caso.diagnostico_secundario} />
                    <InfoItem label="Informe final cargado" value={caso.archivo_caso ? 'Sí' : 'No'} />
                    <InfoItem label="Informe final entregado" value={caso.informe_final_entregado ? 'Sí' : 'No'} />
                  </div>

                  <div className="case-detail-note">
                    <strong>Conclusiones</strong>
                    <p className="mb-0">{caso.observaciones || 'Sin conclusiones registradas.'}</p>
                  </div>
                </div>

                <div className="case-detail-final-report__preview">
                  {caso.archivo_caso ? (
                    <AttachmentPreview
                      filePath={caso.archivo_caso}
                      title="Vista previa del informe final"
                      onOpenImage={handleOpenImage}
                      onDownload={handleDownload}
                    />
                  ) : (
                    <div className="case-detail-empty-preview">
                      <span className="attachment-preview__pdf-icon">
                        <FiFileText aria-hidden="true" />
                      </span>
                      <strong>Sin informe final adjunto</strong>
                      <p className="mb-0">Aun no se cargo un PDF o archivo final para este caso.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : null}
        </section>
      </div>

      {activeImage ? (
        <div className="image-lightbox" role="dialog" aria-modal="true" aria-label={activeImage.name}>
          <button type="button" className="image-lightbox__backdrop" onClick={handleCloseImage} aria-label="Cerrar visor" />
          <div className="image-lightbox__panel">
            <div className="image-lightbox__toolbar">
              <span className="image-lightbox__title">{activeImage.name}</span>
              <div className="image-lightbox__actions">
                <button type="button" className="btn btn-outline-brand btn-sm" onClick={() => setZoomLevel((prev) => Math.max(prev - 0.25, 0.5))}>
                  -
                </button>
                <span className="image-lightbox__zoom-label">{Math.round(zoomLevel * 100)}%</span>
                <button type="button" className="btn btn-outline-brand btn-sm" onClick={() => setZoomLevel((prev) => Math.min(prev + 0.25, 3))}>
                  +
                </button>
                <button type="button" className="btn btn-brand btn-sm" onClick={() => handleDownload(activeImage.src, activeImage.name)}>
                  {downloadingUrl === activeImage.src ? 'Descargando...' : 'Descargar'}
                </button>
                <button type="button" className="btn btn-outline-brand btn-sm" onClick={handleCloseImage}>
                  Cerrar
                </button>
              </div>
            </div>
            <div className="image-lightbox__viewport">
              <img className="image-lightbox__image" src={activeImage.src} alt={activeImage.name} style={imageTransform} />
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default VistaMuestras;
